/* eslint-disable react/prop-types */
import { useState, useEffect, useCallback } from "react";
import { Coffee, LogOut, Loader2, AlertCircle, Play } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { blackUser } from "@/assets";
import { breakInApi, breakOutApi, clockOutApi } from "@/api/staff";
import { toast } from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { useDeviceId } from "@/hooks/useDeviceId";

export default function StaffActionPinModal({
  isOpen,
  onClose,
  actionType = "break_in", // 'break_in' | 'break_out' | 'clock_out'
  user,
  restaurantId,
  onSuccess,
}) {
  const [pin, setPin] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [shake, setShake] = useState(false);

  const navigate = useNavigate();
  const deviceId = useDeviceId();

  const isBreakIn = actionType === "break_in";
  const isBreakOut = actionType === "break_out";
  const isClockOut = actionType === "clock_out";

  const getActionDetails = () => {
    if (isBreakIn) {
      return {
        title: "Staff Break In",
        description: "Enter your PIN to start your break.",
        icon: <Coffee className="w-5 h-5 text-amber-400" />,
        badgeBg: "bg-amber-500/10 border-amber-500/30 text-amber-400",
        btnText: "Confirm Break In",
        btnClass: "bg-amber-500 hover:bg-amber-600 text-black font-semibold",
      };
    }
    if (isBreakOut) {
      return {
        title: "Staff Break Out",
        description: "Enter your PIN to end your break and resume work.",
        icon: <Play className="w-5 h-5 text-emerald-400" />,
        badgeBg: "bg-emerald-500/10 border-emerald-500/30 text-emerald-400",
        btnText: "Confirm Break Out",
        btnClass: "bg-emerald-500 hover:bg-emerald-600 text-black font-semibold",
      };
    }
    return {
      title: "Staff Clock-Out",
      description: "Enter your PIN to clock out for today.",
      icon: <LogOut className="w-5 h-5 text-rose-400" />,
      badgeBg: "bg-rose-500/10 border-rose-500/30 text-rose-400",
      btnText: "Confirm Clock-Out",
      btnClass: "bg-rose-600 hover:bg-rose-700 text-white font-semibold",
    };
  };

  const details = getActionDetails();

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setPin("");
      setError("");
      setShake(false);
      setLoading(false);
    }
  }, [isOpen, actionType]);

  const triggerShake = useCallback((msg) => {
    setError(msg);
    setShake(true);
    setTimeout(() => setShake(false), 500);
  }, []);

  const handleSubmit = useCallback(async () => {
    if (loading) return;

    if (!pin) {
      triggerShake("Please enter your PIN");
      return;
    }

    const userId = user?.system_id || user?.id || user?.user_id;
    const restId =
      restaurantId ||
      user?.restaurant_system_id ||
      user?.restaurant_id ||
      localStorage.getItem("restaurant_system_id") ||
      "";
    const resolvedDeviceId = deviceId || localStorage.getItem("X-Client-Instance") || "";

    if (!userId || !restId) {
      triggerShake("Missing staff or restaurant identification");
      return;
    }

    const payload = {
      user_id: userId,
      restaurant_id: restId,
      pin: pin,
      device_id: resolvedDeviceId,
    };

    setLoading(true);
    setError("");

    try {
      let response;
      if (isBreakIn) {
        response = await breakInApi(payload);
      } else if (isBreakOut) {
        response = await breakOutApi(payload);
      } else {
        response = await clockOutApi(payload);
      }

      if (response?.status === "success") {
        toast.success(response?.message || "Action completed successfully");
        onClose();

        if (isClockOut) {
          const activeRestaurantId = localStorage.getItem("restaurant_system_id") || restId;
          localStorage.removeItem("token");
          localStorage.removeItem("user");
          localStorage.removeItem("tags");
          if (activeRestaurantId) {
            navigate(`/${activeRestaurantId}`);
          } else {
            navigate("/auth/login");
          }
        } else {
          if (typeof onSuccess === "function") {
            onSuccess(response, actionType);
          }
        }
      } else {
        triggerShake(response?.message || "Invalid PIN or failed to process request");
      }
    } catch (err) {
      console.error("Action error:", err);
      triggerShake(err?.response?.data?.message || err?.message || "An unexpected error occurred");
    } finally {
      setLoading(false);
    }
  }, [
    loading,
    pin,
    user,
    restaurantId,
    deviceId,
    isBreakIn,
    isBreakOut,
    isClockOut,
    onClose,
    navigate,
    onSuccess,
    actionType,
    triggerShake,
  ]);

  // Handle keyboard inputs
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e) => {
      if (loading) return;

      const activeElement = document.activeElement;
      if (
        activeElement &&
        (activeElement.tagName === "INPUT" ||
          activeElement.tagName === "TEXTAREA" ||
          activeElement.isContentEditable)
      ) {
        return;
      }

      if (e.key >= "0" && e.key <= "9") {
        e.preventDefault();
        if (pin.length < 8) {
          setPin((prev) => prev + e.key);
          setError("");
        }
      } else if (e.key === "Backspace") {
        e.preventDefault();
        setPin((prev) => prev.slice(0, -1));
        setError("");
      } else if (e.key === "Escape" || e.key === "c" || e.key === "C") {
        if (e.key === "Escape") {
          onClose();
        } else {
          setPin("");
          setError("");
        }
      } else if (e.key === "Enter") {
        e.preventDefault();
        handleSubmit();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, pin, loading, onClose, handleSubmit]);

  const handleKeyPress = (val) => {
    if (loading) return;
    setError("");
    if (val === "clear") {
      setPin("");
    } else if (val === "backspace") {
      setPin((prev) => prev.slice(0, -1));
    } else if (pin.length < 8) {
      setPin((prev) => prev + val);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[360px] bg-[#141416] border border-neutral-800 text-white p-6 rounded-2xl shadow-2xl overflow-hidden select-none">
        <DialogHeader className="flex flex-col items-center text-center gap-1.5 pb-2">
          <div className={`p-3 rounded-full border ${details.badgeBg}`}>
            {details.icon}
          </div>
          <DialogTitle className="text-xl font-bold tracking-tight text-white">
            {details.title}
          </DialogTitle>
          <DialogDescription className="text-xs text-neutral-400">
            {details.description}
          </DialogDescription>
        </DialogHeader>

        {/* Staff Profile Header */}
        <div className="flex items-center gap-3 p-2.5 rounded-xl bg-neutral-900/90 border border-neutral-800/80 my-1">
          <Avatar className="h-9 w-9 rounded-lg border border-neutral-700">
            <AvatarImage
              src={user?.file_manager_url || blackUser}
              alt={user?.name}
              className="object-cover"
            />
            <AvatarFallback className="bg-neutral-800 text-neutral-200 font-bold text-sm">
              {user?.name?.charAt(0) || "U"}
            </AvatarFallback>
          </Avatar>
          <div className="flex flex-col text-left overflow-hidden">
            <span className="font-semibold text-sm text-neutral-100 truncate">
              {user?.name || "Staff Member"}
            </span>
            <span className="text-xs text-neutral-400 capitalize truncate">
              {user?.designation || user?.role || "Staff"}
            </span>
          </div>
        </div>

        {/* PIN Input Display */}
        <div className="flex flex-col items-center gap-2 my-2">
          <div
            className={`w-full h-12 bg-neutral-950/80 border rounded-xl flex items-center justify-center gap-2.5 transition-all ${
              shake
                ? "border-rose-500 animate-shake shadow-[0_0_12px_rgba(244,63,94,0.3)]"
                : error
                ? "border-rose-500/80"
                : "border-neutral-700/80 focus-within:border-amber-400"
            }`}
          >
            {pin.length === 0 ? (
              <span className="text-neutral-500 text-xs tracking-wider">ENTER PIN</span>
            ) : (
              Array.from({ length: 6 }).map((_, i) => (
                <div
                  key={i}
                  className={`w-3.5 h-3.5 rounded-full transition-all duration-150 ${
                    i < pin.length
                      ? "bg-amber-400 scale-110 shadow-[0_0_8px_rgba(251,191,36,0.5)]"
                      : "bg-neutral-800 border border-neutral-700"
                  }`}
                />
              ))
            )}
          </div>

          {error && (
            <div className="flex items-center gap-1.5 text-rose-400 text-xs mt-0.5 text-center">
              <AlertCircle size={14} className="shrink-0" />
              <span>{error}</span>
            </div>
          )}
        </div>

        {/* Numpad */}
        <div className="grid grid-cols-3 gap-2 my-1">
          {["1", "2", "3", "4", "5", "6", "7", "8", "9"].map((num) => (
            <button
              key={num}
              type="button"
              onClick={() => handleKeyPress(num)}
              disabled={loading}
              className="h-11 rounded-xl bg-neutral-900/90 hover:bg-neutral-800 active:scale-95 border border-neutral-800/80 text-lg font-semibold text-white transition-all shadow-sm focus:outline-none"
            >
              {num}
            </button>
          ))}
          <button
            type="button"
            onClick={() => handleKeyPress("clear")}
            disabled={loading}
            className="h-11 rounded-xl bg-neutral-900/60 hover:bg-neutral-800/80 active:scale-95 border border-neutral-800/60 text-sm font-semibold text-rose-400 transition-all focus:outline-none"
          >
            Clear
          </button>
          <button
            type="button"
            onClick={() => handleKeyPress("0")}
            disabled={loading}
            className="h-11 rounded-xl bg-neutral-900/90 hover:bg-neutral-800 active:scale-95 border border-neutral-800/80 text-lg font-semibold text-white transition-all shadow-sm focus:outline-none"
          >
            0
          </button>
          <button
            type="button"
            onClick={() => handleKeyPress("backspace")}
            disabled={loading}
            className="h-11 rounded-xl bg-neutral-900/60 hover:bg-neutral-800/80 active:scale-95 border border-neutral-800/60 text-sm font-semibold text-amber-400 transition-all flex items-center justify-center focus:outline-none"
          >
            ⌫
          </button>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-2.5 mt-3">
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            disabled={loading}
            className="flex-1 bg-neutral-900 border-neutral-800 text-neutral-300 hover:bg-neutral-800 hover:text-white rounded-xl h-11"
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleSubmit}
            disabled={loading || !pin}
            className={`flex-1 rounded-xl h-11 transition-all ${details.btnClass}`}
          >
            {loading ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              details.btnText
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
